/**
 * onlineSession.ts — the rules of ONE online session, from the client's side
 * (M143). PURE: no sockets, no timers, no DOM — Game.ts owns those and asks
 * these functions what a moment MEANS (a welcome, a silent link, a result that
 * arrives twice). Unit-tested in tests/net/onlineSession.test.ts.
 *
 * WHY THIS EXISTS. Reconnecting used to be handled only in two places — the
 * lobby and a live race. A connection that dropped anywhere else (the tour's
 * ready-up screen, the globe hop, a waiting card, the results) was ignored, so
 * the screen waited for ever. The server now keeps a dropped player's seat in
 * every phase and re-sends the result they may have missed; these rules are
 * the client half of that contract.
 */

/** How many automatic rejoin attempts a dropped connection gets before the
 *  player is told (M63). One budget, shared by the socket-closed path and the
 *  link watchdog (M143). */
export const NET_MAX_RETRIES = 8;
/** Snapshot silence that means "the link is dead" while a race streams (M45). */
export const NET_SILENCE_MS = 3000;
/** How long ONE connection attempt may take before the link watchdog may give
 *  up on it and start another (M143). On the recovering phone link that caused
 *  the silence in the first place, the handshake plus the welcome can take
 *  several seconds — the old watchdog fired again every 3 s and killed its own
 *  attempt each time, so a slow reconnect never finished. */
export const NET_ATTEMPT_MS = 8000;

/**
 * Where an online session stands, as the SERVER has told us (M143):
 *   lobby   — welcomed, no race streaming yet (the lobby and its staging beat)
 *   race    — a race is streaming
 *   between — a tour race ended: the ready-up screen, then the globe hop
 *   over    — the last race ended: quick-race results, or a tour's championship
 */
export type NetStage = 'lobby' | 'race' | 'between' | 'over';

/**
 * What Game remembers about ONE online session (M143). It survives an
 * automatic rejoin — the whole point: a reconnect must still know where it
 * stood — and is forgotten when the player really leaves (disconnectOnline).
 */
export interface NetSession {
  stage: NetStage;
  /** A welcome has landed in this session, so any later one is a REJOIN. */
  welcomed: boolean;
  /** This page first met the room while a race was ALREADY running (a reload,
   *  a late join). That race is not announced to us — see `missedBegin`. */
  joinedMidRace: boolean;
  /** The race being staged / running was announced to us ('begin'), or has
   *  already been accounted for. Cleared when that race ends. */
  beginSeen: boolean;
  /** One-shot for Flow (takeMissedBegin): a race started that we were present
   *  for, but whose 'begin' went out while our socket was down. */
  missedBegin: boolean;
  /** The last tour race whose official result we processed (0 = none yet),
   *  and whether that result closed the tour. */
  resultRace: number;
  resultFinal: boolean;
  /** Nothing has happened since that result: no race has streamed and we have
   *  not rejoined. (Lets classifyRaceResult tell a server's early "tour over"
   *  from the result of a race we genuinely missed.) */
  quietSinceResult: boolean;
  /** Votes the server forgets when a seat's socket drops — said again on a
   *  rejoin, so a blip never silently un-readies the player. */
  readySent: boolean;
  rematchSent: boolean;
  /** The last rejoin attempt was answered with an explicit "full". */
  refused: boolean;
  /** Which of the room's races we last knew about (the server counts them;
   *  0 = none yet, or an older server that does not say). */
  raceSeq: number;
}

export function freshNetSession(): NetSession {
  return {
    stage: 'lobby',
    welcomed: false,
    joinedMidRace: false,
    beginSeen: false,
    missedBegin: false,
    resultRace: 0,
    resultFinal: false,
    quietSinceResult: false,
    readySent: false,
    rematchSent: false,
    refused: false,
    raceSeq: 0,
  };
}

/**
 * Did the room start a NEW race while we were away (M143c)? The room numbers
 * its races and says the number in every welcome and every 'begin'. A rejoin
 * that names a different race than the one we knew is a race that was
 * announced while our socket was down — a rematch our friend started, say.
 * The phase cannot tell (both read "racing"); without this the new race paid
 * nothing and its results showed the PREVIOUS race's payout card.
 * (0 on either side = unknown — an older server, or nothing seen yet.)
 */
export function isNewRace(knownSeq: number, welcomeSeq: number | undefined): boolean {
  return (
    typeof welcomeSeq === 'number' && welcomeSeq > 0 && knownSeq > 0 && welcomeSeq !== knownSeq
  );
}

/**
 * The connection cannot be restored (the rejoin budget is spent, or the room
 * no longer holds our session): TELL the player, or just let it go?
 *
 * Mid-session they must be told — that is what gives every screen a way out,
 * including the tour's globe hop, which has no button of its own. But once the
 * session's last race is OVER there is nothing left to resume: the results (or
 * the championship) on screen are still true, so the connection is let go
 * quietly instead of covering them with an error. The one exception is a
 * REMATCH the player is waiting on — that wait would otherwise never end.
 */
export function planGiveUp(stage: NetStage, rematchPending: boolean): 'tell' | 'letGo' {
  return stage === 'over' && !rematchPending ? 'letGo' : 'tell';
}

/**
 * What a join handshake means for the session we were in (M143). `phase` is
 * the room's phase riding the welcome; `welcomedBefore` says this is a REJOIN.
 *
 *   rebuild — build (or rebuild) the race from the welcome. Every first
 *             handshake, and any rejoin into a lobby we were still in or a
 *             race that is being staged / running.
 *   adopt   — the room kept our seat but is BETWEEN races (ready-up, globe
 *             hop) or on its results: there is no race to rebuild, so just
 *             take the new socket. The server re-sends the result we may have
 *             missed, and the next race arrives as a 'restart' like any other.
 *   lost    — the room is back in its lobby but we were past it: the session
 *             we were in is gone (the room emptied or restarted). Tell the
 *             player rather than strand them in a lobby nobody else is in.
 *   letGo   — the same, after the last race: nothing left to resume, so the
 *             screen they are reading stays (see planGiveUp).
 *   refuse  — a NEW session (this page left the room for real, then came back
 *             to it) was let into a room that is between races or on its
 *             results. The room only did so because this tab still holds its
 *             old seat's token; there is no race here for a new session to
 *             join and nothing of the old one left to resume. Treat it as the
 *             room saying "not now", exactly as it did before seats could be
 *             reclaimed in those phases.
 */
export function planWelcome(
  phase: string | undefined,
  stage: NetStage,
  welcomedBefore: boolean,
  rematchPending: boolean,
): 'rebuild' | 'adopt' | 'lost' | 'letGo' | 'refuse' {
  const betweenRaces = phase === 'intermission' || phase === 'globe' || phase === 'ended';
  if (!welcomedBefore) return betweenRaces ? 'refuse' : 'rebuild';
  if (betweenRaces) return 'adopt';
  if (phase === 'lobby' && stage !== 'lobby') {
    return planGiveUp(stage, rematchPending) === 'letGo' ? 'letGo' : 'lost';
  }
  return 'rebuild';
}

/**
 * One link-watchdog decision (M143). The watchdog exists because a dead
 * connection can stay "open" in the browser for a minute; 3 s without a
 * snapshot mid-race is the real signal (M45).
 *
 *   idle      — snapshots are arriving.
 *   wait      — silent, but a rejoin is already under way (a retry is queued,
 *               or the current attempt is still inside its time allowance):
 *               starting another now would only kill the one in flight.
 *   reconnect — silent and nothing is trying: rejoin now.
 *   giveUp    — silent and the rejoin budget is spent.
 */
export function planWatchdog(a: {
  /** ms since the last snapshot landed (either channel). */
  silentMs: number;
  /** A scheduled retry is waiting to fire. */
  retryQueued: boolean;
  /** The current socket has been welcomed (false = an attempt in flight). */
  welcomed: boolean;
  /** ms since the current connection attempt began. */
  attemptMs: number;
  /** Rejoin attempts already used. */
  retries: number;
}): 'idle' | 'wait' | 'reconnect' | 'giveUp' {
  if (a.silentMs <= NET_SILENCE_MS) return 'idle';
  if (a.retryQueued || (!a.welcomed && a.attemptMs < NET_ATTEMPT_MS)) return 'wait';
  return a.retries >= NET_MAX_RETRIES ? 'giveUp' : 'reconnect';
}

/** How often the client checks that its connection is alive (M143). */
export const NET_PING_MS = 4000;
/** No message of ANY kind for this long on an "open" socket means the link is
 *  dead and nobody told us (M143). Three missed pings. */
export const NET_QUIET_MS = 12_000;

/**
 * One connection-liveness check (M143), every NET_PING_MS for as long as the
 * player is online — in the lobby, between tour races, on the results, and
 * during a race (where the faster snapshot watchdog usually acts first).
 *
 * WHY. A browser WebSocket has no heartbeat of its own. When a network path
 * dies without a goodbye (the router is still there, the internet behind it is
 * not; a phone moves between Wi-Fi and cellular) the socket stays "open" for
 * as long as nothing is sent — and between races nothing IS sent. The player
 * sat on a ready-up screen or a globe flight that could never end. So we ask
 * the server a tiny question every few seconds; any answer proves the link.
 *
 *   skip      — nothing to do: the tab is hidden (its timers barely run, and
 *               coming back to the foreground has its own check), a retry is
 *               already queued, or an attempt is still inside its allowance.
 *   ping      — the link has spoken recently: ask again.
 *   reconnect — an "open" socket has been silent too long, or a connection
 *               attempt has hung: start a fresh one.
 *   giveUp    — the same, with the rejoin budget spent.
 */
export function planLiveness(a: {
  /** The socket as the browser reports it. */
  socket: 'connecting' | 'open' | 'closed';
  /** The tab is hidden. */
  hidden: boolean;
  /** A scheduled retry is waiting to fire. */
  retryQueued: boolean;
  /** ms since ANY message arrived on this socket (or since it was opened). */
  quietMs: number;
  /** ms since the current connection attempt began. */
  attemptMs: number;
  /** Rejoin attempts already used. */
  retries: number;
}): 'skip' | 'ping' | 'reconnect' | 'giveUp' {
  if (a.hidden || a.retryQueued || a.socket === 'closed') return 'skip';
  if (a.socket === 'connecting') {
    if (a.attemptMs < NET_ATTEMPT_MS) return 'skip';
  } else if (a.quietMs <= NET_QUIET_MS) {
    return 'ping';
  }
  return a.retries >= NET_MAX_RETRIES ? 'giveUp' : 'reconnect';
}

/**
 * How to treat a tour {t:'raceResult'} (M143). The server RE-SENDS the last
 * result to a seat that reconnects between races, so an exact repeat must be a
 * complete no-op — no second standings row, no second fanfare, no ready-up
 * screen rebuilt under the player.
 *
 *   new         — a race we have no result for yet.
 *   repeatFinal — the result we already have, now marked as the tour's LAST
 *                 (a server shutting down ends the tour early): show the
 *                 championship, but record and pay nothing again.
 *   duplicate   — an exact repeat: ignore it.
 *
 * `seen` is the result we hold: its race number (0 = none), whether it was a
 * final, and whether anything has happened since (`quiet` = no race streamed,
 * no rejoin). That last one catches a shutdown during the globe hop, which
 * labels the old places with the NEXT race's number: a "final" for the next
 * race when no race has run since cannot be a new result.
 */
export function classifyRaceResult(
  seen: { race: number; final: boolean; quiet: boolean },
  raceNum: number,
  last: boolean,
): 'new' | 'repeatFinal' | 'duplicate' {
  if (raceNum === seen.race) return last && !seen.final ? 'repeatFinal' : 'duplicate';
  if (last && seen.quiet && seen.race > 0 && raceNum === seen.race + 1) return 'repeatFinal';
  return 'new';
}

/**
 * Put the SERVER's official finish times on a results table (M143c). The
 * table is read off the client's own copy of the world, which can be a frame
 * old — or, after a dropped connection, a whole outage old: racers who
 * finished meanwhile showed "—". `times[racerId]` is the finish time in ms,
 * `null` for a racer the server says did not finish, and absent for a racer
 * (or a whole server) that gave none — those rows keep what the table had.
 */
export function withOfficialTimes<
  E extends { racerId: number; finished: boolean; timeMs: number | null },
>(entries: E[], times: (number | null | undefined)[]): E[] {
  return entries.map((e) => {
    const t = times[e.racerId];
    if (t === undefined) return e;
    return typeof t === 'number' && Number.isFinite(t)
      ? { ...e, finished: true, timeMs: Math.round(t) }
      : { ...e, finished: false, timeMs: null };
  });
}

/**
 * Re-rank a results table by the SERVER's official places (M143). The table
 * itself is built from the client's predicted world — good for names, colours
 * and times, but its ORDER is a guess: the last snapshot of a race can arrive
 * after the "race over" message (or not at all), and the prediction's own
 * finish counter is not synced. The server's places are the result. A racer
 * the server did not place keeps the local guess.
 */
export function inOfficialOrder<E extends { racerId: number; place: number; isPlayer: boolean }>(
  result: { entries: E[]; playerPlace: number },
  places: number[],
): { entries: E[]; playerPlace: number } {
  const entries = result.entries
    .map((e) => ({ ...e, place: places[e.racerId] ?? e.place }))
    .sort((a, b) => a.place - b.place);
  return {
    entries,
    playerPlace: entries.find((e) => e.isPlayer)?.place ?? result.playerPlace,
  };
}
